import React, { useState, useEffect } from 'react';
import { FileText, Eye, Lock } from 'lucide-react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { getAccountById } from '../services/firestoreService';
import { fetchTemplateStructure } from '../services/hrStructureService';
import {
  fetchHRTemplates,
  hasTemplateFile,
  findTemplateByTitle,
  getTemplateViewUrl,
  findTemplateDoc,
} from '../utils/hrTemplates';
import StartSubpageLayout from '../components/start/StartSubpageLayout';
import DocumentViewerModal from '../components/DocumentViewerModal';
import BBALoader from '../components/BBALoader';

/**
 * Free-sample view of the portal Sales ▸ Materials tab for /start prospects.
 *
 * Sections and cards come from the same CRM-managed catalog the portal renders
 * (templatesStructures/sales + templatesCollections docs with pillar 'Sales'),
 * so this page cannot drift as the library changes. Which cards are unlocked is
 * driven by `accounts/{id}.unlockedSalesMaterials` — an array of catalog keys
 * (the structure item's `title`) granted from CRM ▸ Lead Overview. Same
 * semantics as unlockedSOPs: missing field = everything unlocked (legacy /
 * promoted accounts), empty array with the matching freePage = everything
 * unlocked (grant saved before any items were picked), otherwise an allowlist.
 * Unlocked cards are view-only — Tailor stays a member feature.
 */
const StartSalesMaterials = () => {
  const { darkMode } = useTheme();
  const { currentUser } = useAuth();

  const [sections, setSections] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [unlockedMaterials, setUnlockedMaterials] = useState(null); // null = all unlocked
  const [loading, setLoading] = useState(true);
  const [viewerDoc, setViewerDoc] = useState(null);

  useEffect(() => {
    if (!currentUser) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const [structure, docs] = await Promise.all([
          fetchTemplateStructure('sales', []),
          fetchHRTemplates(),
        ]);
        if (cancelled) return;
        setSections(structure);
        setDocuments(docs.filter((d) => (d.pillar || 'HR') === 'Sales'));

        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
        const accountId = userDoc.exists() ? userDoc.data().accountId : null;
        if (accountId) {
          const accountData = await getAccountById(accountId);
          if (cancelled || !accountData) return;
          const isFullPortalClient =
            accountData.program?.portalAccess === true ||
            accountData.program?.clientStatus === 'Active' ||
            accountData.stage === 'Portal Client' ||
            accountData.substatus === 'Portal Signup Completed';
          if (!isFullPortalClient && accountData.unlockedSalesMaterials !== undefined) {
            const list = Array.isArray(accountData.unlockedSalesMaterials)
              ? accountData.unlockedSalesMaterials
              : [];
            setUnlockedMaterials(
              list.length === 0 && accountData.freePage === 'sales/resources' ? null : list
            );
          }
        }
      } catch (error) {
        console.error('StartSalesMaterials: error loading materials', error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [currentUser]);

  const isUnlocked = (item) =>
    unlockedMaterials === null || unlockedMaterials.includes(item.title);

  const handleView = (item) => {
    const url = getTemplateViewUrl(findTemplateByTitle(documents, item.title));
    if (!url) {
      alert('This template is not available yet. Please check back later.');
      return;
    }
    setViewerDoc({ url, title: item.name || item.title });
  };

  const populatedSections = sections.filter((s) => (s.items || []).length > 0);
  const unlockedCount = populatedSections.reduce(
    (count, s) => count + s.items.filter(isUnlocked).length,
    0
  );

  const ghostBtn = `flex-1 inline-flex items-center justify-center gap-1 px-2 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
    darkMode ? 'border-gray-700 text-gray-200 hover:bg-gray-700/60' : 'border-gray-200 text-gray-700 hover:bg-gray-50'
  }`;
  const disabledBtn = `flex-1 inline-flex items-center justify-center gap-1 px-2 py-1.5 rounded-lg text-xs font-medium border ${
    darkMode ? 'border-gray-700 text-gray-600' : 'border-gray-200 text-gray-300'
  } cursor-not-allowed`;

  return (
    <StartSubpageLayout
      title="Sales Materials"
      subtitle={
        unlockedMaterials === null
          ? 'Templates and resources from our Sales library'
          : `${unlockedCount} resource${unlockedCount === 1 ? '' : 's'} unlocked for you — members get the full library`
      }
    >
      {loading ? (
        <div className="flex justify-center py-16">
          <BBALoader size={40} />
        </div>
      ) : populatedSections.length === 0 ? (
        <div
          className={`rounded-xl border p-8 text-center ${
            darkMode ? 'border-gray-800 bg-gray-900' : 'border-gray-200 bg-white'
          }`}
        >
          <FileText className={`w-8 h-8 mx-auto mb-3 ${darkMode ? 'text-gray-600' : 'text-gray-300'}`} />
          <h3 className={`text-base font-semibold mb-1 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            No materials yet
          </h3>
          <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            Sales materials will appear here once they are published.
          </p>
        </div>
      ) : (
        populatedSections.map((section) => (
          <div key={section.id} className="mt-10 first:mt-0">
            <div className="flex items-center justify-center gap-3 mb-5">
              <span className={`h-px flex-1 max-w-[110px] ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`} />
              <span className="shrink-0 w-10 h-10 rounded-xl bg-red-50 dark:bg-red-900/30 flex items-center justify-center">
                {section.emoji ? (
                  <span className="text-xl leading-none">{section.emoji}</span>
                ) : (
                  <FileText className="w-5 h-5 text-red-600 dark:text-red-400" />
                )}
              </span>
              <h2 className={`text-xl md:text-2xl font-bold tracking-tight leading-tight text-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                {section.label}
              </h2>
              <span className={`h-px flex-1 max-w-[110px] ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`} />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {section.items.map((item) => {
                const unlocked = isUnlocked(item);
                const available = unlocked && hasTemplateFile(documents, item.title);
                const version = String(findTemplateDoc(documents, item.title)?.version || '').trim();
                return (
                  <div
                    key={item.title}
                    className={`rounded-xl border p-4 transition-shadow ${
                      darkMode ? 'border-gray-800 bg-gray-900' : 'border-gray-200 bg-white'
                    } ${unlocked ? 'hover:shadow-md' : 'opacity-70'}`}
                  >
                    <div className="flex items-center gap-3 mb-4">
                      <span className="shrink-0 w-11 h-11 rounded-lg bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 flex items-center justify-center">
                        {unlocked ? (
                          item.emoji ? (
                            <span className="text-2xl leading-none">{item.emoji}</span>
                          ) : (
                            <FileText className="w-5 h-5" />
                          )
                        ) : (
                          <Lock className="w-5 h-5" />
                        )}
                      </span>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 min-w-0">
                          <h4 className={`text-base md:text-lg font-bold leading-snug truncate ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                            {item.name || item.title}
                          </h4>
                          {unlocked && version && (
                            <span
                              className={`shrink-0 px-1.5 py-0.5 rounded-full text-[10px] font-semibold leading-none ${
                                darkMode ? 'bg-gray-800 text-gray-400 border border-gray-700' : 'bg-gray-100 text-gray-500 border border-gray-200'
                              }`}
                            >
                              v{version}
                            </span>
                          )}
                        </div>
                        {item.description && (
                          <p className={`text-xs mt-0.5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{item.description}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {unlocked ? (
                        <button
                          type="button"
                          onClick={() => handleView(item)}
                          disabled={!available}
                          title={available ? 'View document' : 'File not available yet'}
                          className={available ? ghostBtn : disabledBtn}
                        >
                          <Eye className="w-3.5 h-3.5" />
                          View
                        </button>
                      ) : (
                        <span
                          className={disabledBtn}
                          title="Available to members — talk to us about unlocking the full library"
                        >
                          <Lock className="w-3.5 h-3.5" />
                          Members only
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))
      )}

      <DocumentViewerModal
        isOpen={!!viewerDoc}
        onClose={() => setViewerDoc(null)}
        url={viewerDoc?.url}
        title={viewerDoc?.title}
      />
    </StartSubpageLayout>
  );
};

export default StartSalesMaterials;
